import type { JSX, KeyboardEvent } from 'react';
import { Fragment, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Menu,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ApiError, benchmarkApi, costsApi, dinApi, type CostGroup, type CostItem, type CostItemInput, type CostStage, type CostStageTotals, type CostTotals, undoPaths } from '../../api';
import { COST_DEPTHS, COST_STAGES, formatAmount, formatDate, formatEuro, formatPercent, formatQuantity } from '../../format';
import { AmountCell } from '../../ui/AmountCell';
import { EditableText } from '../../ui/EditableText';
import { Section } from '../../ui/Section';
import { useContextMenu } from '../../ui/ContextMenu';
import { BenchmarkCatalog } from './BenchmarkCatalog';
import { CompareView } from './CompareView';
import { CostEstimatePanel } from './CostEstimatePanel';
import { ValuesPanel } from './ValuesPanel';
import { VariantsPanel } from './VariantsPanel';
import { inVariant, resultFor, storeVariant, storedVariant } from './variant';
import { guardedStep, inversePatch, useUndo } from '../../ui/undo';
import { RegionalPanel } from './RegionalPanel';
import { type DepthChoice, expandedForDepth, visibleCodes } from './tree';
import { usePageDownloads } from '../../export/downloads';

/**
 * Cost planning after DIN 276 (module 3).
 *
 * One table, read as an estimator reads it: cost group down the side, the cost
 * stages across, orders placed and the control bar on the right. A group with
 * planned children shows their sum - greyed, since it is calculated - and only
 * the groups without children are typed into.
 */
export function CostsPage(): JSX.Element {
  const { projectId = '' } = useParams();
  const queryClient = useQueryClient();
  const plan = useQuery({ queryKey: ['costs', projectId], queryFn: () => costsApi.get(projectId) });
  const me = queryClient.getQueryData<{ permissions: string[] }>(['me']);
  const canEdit = me?.permissions.includes('costs.write') ?? false;
  const isAdmin = me?.permissions.includes('users.manage') ?? false;
  const catalogAccess = useQuery({ queryKey: ['benchmarks', 'me'], queryFn: benchmarkApi.me });
  const [gross, setGross] = useState(false);
  const [chosen, setChosen] = useState<string>(() => storedVariant(projectId) ?? '');
  const [comparing, setComparing] = useState(false);
  const [editVariants, setEditVariants] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const choose = (id: string): void => { setChosen(id); setComparing(false); storeVariant(projectId, id); };
  const [open, setOpen] = useState<{ groupId: string; stage: CostStage } | null>(null);
  // The stage chosen with a tile; null = the one where the work is (see below).
  const [picked, setPicked] = useState<CostStage | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  // The tree (AP-86): how deep, what is folded, whether empty groups and the lines show.
  const [view, setView] = useState<TreeView>(() => storedTree(projectId));
  const keepView = (next: TreeView): void => { setView(next); storeTree(projectId, next); };
  const din = useQuery({ queryKey: ['din'], queryFn: dinApi.list, staleTime: 3_600_000 });
  const csvVariant = storedVariant(projectId) ?? '';
  usePageDownloads('costs', [{ key: 'csv', label: 'Kostenplan als CSV', hint: 'alle Stufen, Positionen und Werte der gewählten Variante', href: costsApi.csvUrl(projectId, chosen || csvVariant || undefined) }]);

  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['costs', projectId] });
  const onError = { onSettled: refresh };
  const updatePlan = useMutation({ mutationFn: (body: { vatRate?: number; referenceStage?: CostStage }) => costsApi.updatePlan(projectId, body), ...onError });
  const firstLevel = useMutation({ mutationFn: () => costsApi.firstLevel(projectId), ...onError });
  const outline = useMutation({ mutationFn: (depth: 1 | 2 | 3) => costsApi.outline(projectId, depth), ...onError });
  const addGroup = useMutation({
    mutationFn: () => costsApi.addGroup(projectId, { code: code.trim(), name: name.trim() }),
    onSuccess: () => { setCode(''); setName(''); },
    ...onError,
  });
  const undo = useUndo();
  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Parameters<typeof costsApi.updateGroup>[2]) => {
      const g = plan.data?.groups.find((x) => x.id === id);
      const step = g
        ? guardedStep(`Kostengruppe ${g.code}`, 'PATCH', undoPaths.costGroup(projectId, id), inversePatch(body, { code: g.code, name: g.name, note: g.note, ...g.own }), body)
        : null;
      return undo.recordAfter(costsApi.updateGroup(projectId, id, body), step);
    },
    ...onError,
  });
  const remove = useMutation({ mutationFn: (id: string) => costsApi.removeGroup(projectId, id), ...onError });
  // Lines shown under their group: clear quantity or rate, or remove them, by right-click (as in the Kostenermittlung).
  const updateItem = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & CostItemInput) => {
      const before = plan.data?.items.find((i) => i.id === id);
      const step = before
        ? guardedStep(`Position ${before.description.slice(0, 40)}`, 'PATCH', undoPaths.costItem(projectId, id), inversePatch(body, before as unknown as Record<string, unknown>), body)
        : null;
      return undo.recordAfter(costsApi.updateItem(projectId, id, body), step);
    },
    ...onError,
  });
  const removeItem = useMutation({ mutationFn: (id: string) => costsApi.removeItem(projectId, id), ...onError });
  const error = [updatePlan.error, firstLevel.error, outline.error, addGroup.error, update.error, remove.error, updateItem.error, removeItem.error].find(Boolean);
  const lineMenu = useContextMenu<{ item: CostItem; stage: CostStage }>(({ item: l, stage }, close) => {
    const run = (f: () => void) => () => { close(); f(); };
    return (
      <>
        <Menu.Label title={l.description}>{l.description.length > 40 ? `${l.description.slice(0, 40)} …` : l.description || 'Position'}</Menu.Label>
        <Menu.Item onClick={run(() => setOpen({ groupId: l.groupId, stage }))}>In der Kostenermittlung bearbeiten</Menu.Item>
        {canEdit && (
          <>
            <Menu.Divider />
            <Menu.Item disabled={!l.quantityFormula && !l.takeoff?.length} onClick={run(() => updateItem.mutate({ id: l.id, quantityFormula: null, takeoff: null }))}>Menge löschen</Menu.Item>
            <Menu.Item disabled={!l.unitPriceFormula} onClick={run(() => updateItem.mutate({ id: l.id, unitPriceFormula: null, benchmarkId: null }))}>Kennwert löschen</Menu.Item>
            <Menu.Divider />
            <Menu.Item color="red" onClick={run(() => { if (window.confirm(`Position „${l.description || 'ohne Bezeichnung'}“ löschen?`)) removeItem.mutate(l.id); })}>Position löschen …</Menu.Item>
          </>
        )}
      </>
    );
  });

  const context = useContextMenu<CostGroup>((group, close) => {
    const next = suggestChild(group.code, plan.data?.groups ?? []);
    return (
      <>
        <Menu.Label>{group.code} {group.name}</Menu.Label>
        <Menu.Item onClick={() => { close(); setOpen({ groupId: group.id, stage: plan.data?.referenceStage ?? 'calculation' }); }}>Kostenermittlung öffnen</Menu.Item>
        {canEdit && next && (
          <Menu.Item onClick={() => { close(); setCode(next); setName(''); }}>Untergruppe {next} anlegen …</Menu.Item>
        )}
        {canEdit && (
          <>
            <Menu.Divider />
            <Menu.Item color="red" onClick={() => { close(); remove.mutate(group.id); }}>Kostengruppe löschen</Menu.Item>
          </>
        )}
      </>
    );
  });

  if (plan.isPending) return <Center h={200}><Loader /></Center>;
  if (plan.isError) return <Alert color="red">Die Kostenplanung konnte nicht geladen werden.</Alert>;
  const data = plan.data;
  const result = resultFor(data, chosen);
  const variantId = result.variantId;
  const variant = data.variants.find((v) => v.id === variantId);
  const rollup = result.rollup;
  const factor = gross ? 1 + data.vatRate / 100 : 1;
  const show = (v: number | null): number | null => (v === null ? null : Math.round(v * factor * 100) / 100);
  const totals = new Map(rollup.groups.map((g) => [g.code, g]));
  const reference = data.referenceStage;
  const referenceLabel = COST_STAGES.find((s) => s.key === reference)!.label;
  const items = data.items.filter((i) => inVariant(i, variantId));
  const itemCount = (groupId: string): number => items.filter((i) => i.groupId === groupId).length;
  const risk = variant?.riskPercent ?? data.riskPercent;
  const range = variant?.rangePercent ?? data.rangePercent;
  const stageTotals = (key: CostStage): CostStageTotals => result.totals[key];
  // Show the stage where the work is: the open panel's, or the most detailed one with lines.
  const busiest = [...COST_STAGES].reverse().find((s) => items.some((i) => i.stage === s.key))?.key ?? reference;
  const focusStage = picked ?? open?.stage ?? busiest;
  // One stage for every view below: the tiles switch it, the estimate panel follows and leads.
  const pick = (stage: CostStage): void => {
    setPicked(stage);
    if (open) setOpen({ ...open, stage });
  };
  const nodes = rollup.groups.map((g) => ({ code: g.code, parent: g.parent, depth: g.depth }));
  const expanded = view.expanded ? new Set(view.expanded) : expandedForDepth(nodes, view.depth);
  const hasKids = new Set(nodes.map((n) => n.parent).filter((p): p is string => p !== null));
  const groupByCode = new Map(data.groups.map((g) => [g.code, g]));
  const used = (c: string): boolean => {
    const t = totals.get(c);
    const g = groupByCode.get(c);
    return (t !== undefined && (t.committed > 0 || COST_STAGES.some((s) => t.stages[s.key] !== null))) || (g !== undefined && items.some((i) => i.groupId === g.id));
  };
  const shownCodes = new Set(visibleCodes(nodes, expanded, view.onlyUsed ? used : undefined));
  const toggle = (c: string): void => {
    const next = new Set(expanded);
    if (next.has(c)) next.delete(c); else next.add(c);
    keepView({ ...view, expanded: [...next] });
  };
  const dinByCode = new Map((din.data ?? []).map((g) => [g.code, g]));
  const dinMissing = (din.data ?? []).filter((g) => !groupByCode.has(g.code)).length;
  const lineRows = (group: CostGroup): JSX.Element[] => {
    if (!(view.lines || view.linesOf.includes(group.id))) return [];
    return items.filter((i) => i.groupId === group.id && i.stage === focusStage).map((l) => {
      const r = result.lines[l.id];
      const depth = (totals.get(group.code)?.depth ?? 0) + 1;
      return (
        <Table.Tr key={l.id} style={{ cursor: 'pointer' }} onClick={() => setOpen({ groupId: group.id, stage: focusStage })} onContextMenu={(e) => lineMenu.open(e, { item: l, stage: focusStage })} title="Position – anklicken zum Bearbeiten, Rechtsklick für Menge, Kennwert, Löschen">
          {canEdit && <Table.Td />}
          <Table.Td />
          <Table.Td>
            <Text size="xs" style={{ paddingLeft: depth * 14 + 18 }} c="dimmed" lineClamp={1}>
              {l.description || '—'}
              {r && r.quantity !== null ? ` · ${formatQuantity(r.quantity)} ${l.unit ?? ''}${r.unitPrice !== null ? ` × ${formatAmount(r.unitPrice)}` : ''}` : ''}
            </Text>
          </Table.Td>
          {COST_STAGES.map((s) => (
            <Table.Td key={s.key} style={s.key === focusStage ? { background: 'var(--mantine-color-brand-light)' } : undefined}>
              {s.key === focusStage && <AmountCell value={r?.amount === null || r?.amount === undefined ? null : show(r.amount)} readOnly dimmed />}
            </Table.Td>
          ))}
          <Table.Td />
          <Table.Td />
        </Table.Tr>
      );
    });
  };

  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
      <Stack gap="md" pb="xl">
        {error && <Alert color="red" withCloseButton onClose={() => [updatePlan, firstLevel, outline, addGroup, update, remove].forEach((m) => m.reset())}>{error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen.'}</Alert>}

        {data.variants.length > 0 && (
          <Group gap="sm" wrap="wrap">
            <VariantChooser variants={data.variants} value={comparing ? 'compare' : variantId} onChange={(v) => (v === 'compare' ? setComparing(true) : choose(v))} />
            {variant?.description && !comparing && <Text size="xs" c="dimmed" lineClamp={2} title={variant.description} style={{ flex: 1, minWidth: 200 }}>{variant.description}</Text>}
            <Anchor size="xs" component="button" type="button" onClick={() => setEditVariants((v) => !v)}>Varianten bearbeiten</Anchor>
          </Group>
        )}
        {editVariants && <VariantsPanel projectId={projectId} plan={data} canEdit={canEdit} onClose={() => setEditVariants(false)} onCreated={(id) => choose(id)} />}
        {comparing && data.variants.length > 1 && <CompareView projectId={projectId} plan={data} stage={focusStage} onStage={pick} />}

        <RegionalPanel projectId={projectId} plan={data} canEdit={canEdit} />

        <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="sm">
          {COST_STAGES.map((stage) => {
            const t = stageTotals(stage.key);
            return (
              <Tile
                key={stage.key}
                label={stage.label}
                value={show(risk > 0 ? t.withRisk : t.net)}
                highlight={stage.key === reference}
                active={stage.key === focusStage}
                onClick={() => pick(stage.key)}
                note={risk > 0 && t.net !== null ? `mit ${formatQuantity(risk)} % Risiko` : undefined}
              />
            );
          })}
          <Tile label="Beauftragt" value={show(rollup.committedTotal)} note={rollup.total[reference] ? formatPercent((rollup.committedTotal / rollup.total[reference]!) * 100) + ` der ${COST_STAGES.find((s) => s.key === reference)!.short}` : undefined} />
        </SimpleGrid>

        <Section
          title={`Kostengruppen nach DIN 276${variant ? ` · ${variant.code} ${variant.name}` : ''}`}
          description={`Beträge ${gross ? `brutto (inkl. ${data.vatRate} % USt.)` : 'netto'}. Eine Gruppe zählt ihre eigenen Positionen und ihre Untergruppen zusammen. Kostenkontrolle gegen: ${referenceLabel}${variant && !variant.isPrimary ? `, Variante ${data.variants.find((v) => v.isPrimary)?.code ?? ''}` : ''}.`}
          actions={
            <>
              {catalogAccess.data?.allowed && (
                <Button size="xs" variant="light" leftSection={<span aria-hidden>⌕</span>} onClick={() => setCatalogOpen(true)} title="Kennwerte des Büros, z. B. BKI – suchen, einfügen, freigeben">
                  Kennwertkatalog
                </Button>
              )}
              <SegmentedControl size="xs" value={gross ? 'gross' : 'net'} onChange={(v) => setGross(v === 'gross')} data={[{ value: 'net', label: 'netto' }, { value: 'gross', label: 'brutto' }]} />
              <Menu position="bottom-end" withinPortal>
                <Menu.Target><Button size="xs" variant="default">Einstellungen</Button></Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Kostenkontrolle gegen</Menu.Label>
                  {COST_STAGES.map((s) => (
                    <Menu.Item key={s.key} disabled={!canEdit} onClick={() => updatePlan.mutate({ referenceStage: s.key })} leftSection={<span style={{ width: 12, display: 'inline-block' }}>{s.key === reference ? '✓' : ''}</span>}>{s.label}</Menu.Item>
                  ))}
                  <Menu.Divider />
                  <Menu.Label>Umsatzsteuer</Menu.Label>
                  {[19, 7, 0].map((rate) => (
                    <Menu.Item key={rate} disabled={!canEdit} onClick={() => updatePlan.mutate({ vatRate: rate })} leftSection={<span style={{ width: 12, display: 'inline-block' }}>{rate === data.vatRate ? '✓' : ''}</span>}>{rate} %</Menu.Item>
                  ))}
                  <Menu.Divider />
                  <Menu.Item onClick={() => setEditVariants(true)}>{data.variants.length > 0 ? 'Varianten, Risiko und Bandbreite …' : 'Risiko, Bandbreite, Varianten …'}</Menu.Item>
                  {catalogAccess.data?.allowed && <Menu.Item onClick={() => setCatalogOpen(true)}>Kennwertkatalog …</Menu.Item>}
                </Menu.Dropdown>
              </Menu>
            </>
          }
        >
          {data.groups.length === 0 ? (
            <Stack align="center" gap="xs" py="lg">
              <Text size="sm" c="dimmed">Noch keine Kostengruppen.</Text>
              {canEdit && (din.data?.length ?? 0) > 0 && (
                <Group gap="xs">
                  <Button onClick={() => outline.mutate(3)} loading={outline.isPending}>Gliederung nach DIN 276 anlegen (alle {din.data!.length} Kostengruppen)</Button>
                  <Button variant="default" onClick={() => outline.mutate(2)} loading={outline.isPending}>nur bis zur 2. Ebene</Button>
                  <Button variant="default" onClick={() => outline.mutate(1)} loading={outline.isPending}>nur 100 bis 800</Button>
                </Group>
              )}
              {canEdit && (din.data?.length ?? 0) === 0 && <Button onClick={() => firstLevel.mutate()} loading={firstLevel.isPending}>Gliederung anlegen (100 bis 800)</Button>}
              {canEdit && din.isSuccess && din.data.length === 0 && <Text size="xs" c="dimmed">Die vollständige Gliederung erscheint hier, sobald die Administration die DIN-276-Liste unter „Verwaltung › Bibliotheken › DIN 276“ eingelesen hat.</Text>}
            </Stack>
          ) : (
            <>
            <Group gap="sm" wrap="wrap" pb={4}>
              <Text size="xs" c="dimmed">Ebenen</Text>
              <SegmentedControl size="xs" value={String(view.expanded ? 'eigen' : view.depth)} onChange={(v) => v !== 'eigen' && keepView({ ...view, depth: (v === 'all' ? 'all' : Number(v)) as DepthChoice, expanded: null })}
                data={[...COST_DEPTHS, ...(view.expanded ? [{ value: 'eigen', label: 'eigene' }] : [])]} />
              <Switch size="xs" label="nur belegte Kostengruppen" checked={view.onlyUsed} onChange={(e) => keepView({ ...view, onlyUsed: e.currentTarget.checked })} />
              <Switch size="xs" label={`Positionen zeigen (${COST_STAGES.find((s) => s.key === focusStage)!.short})`} checked={view.lines} onChange={(e) => keepView({ ...view, lines: e.currentTarget.checked, linesOf: [] })} />
            </Group>
            <Table.ScrollContainer minWidth={1100}>
              <Table verticalSpacing={4} highlightOnHover striped={false} className="pst-tabular">
                <Table.Thead>
                  <Table.Tr>
                    {canEdit && <Table.Th w={30} />}
                    <Table.Th w={80}>KG</Table.Th>
                    <Table.Th>Bezeichnung</Table.Th>
                    {COST_STAGES.map((s) => (
                      <Table.Th key={s.key} w={120} ta="right" title={`${s.label} – anklicken, um diese Stufe zu wählen`} c={s.key === reference || s.key === focusStage ? undefined : 'dimmed'}
                        style={{ cursor: 'pointer', ...(s.key === focusStage ? { background: 'var(--mantine-color-brand-light)' } : {}) }} onClick={() => pick(s.key)}>{s.short} (€)</Table.Th>
                    ))}
                    <Table.Th w={120} ta="right">Beauftragt (€)</Table.Th>
                    <Table.Th w={170}>Kontrolle</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.groups.filter((group) => shownCodes.has(group.code)).map((group) => {
                    const t = totals.get(group.code)!;
                    const hasChildren = hasKids.has(group.code);
                    const dinNote = dinByCode.get(group.code)?.note;
                    const lineCount = items.filter((i) => i.groupId === group.id && i.stage === focusStage).length;
                    return (
                      <Fragment key={group.id}>
                      <Table.Tr onContextMenu={(e) => context.open(e, group)} style={t.depth === 0 ? { background: 'var(--mantine-color-default-hover)' } : undefined}>
                        {canEdit && (
                          <Table.Td>
                            <ActionIcon size="xs" variant="subtle" color="gray" aria-label={`Kostengruppe ${group.code} löschen`} title="Kostengruppe löschen" onClick={() => remove.mutate(group.id)}>✕</ActionIcon>
                          </Table.Td>
                        )}
                        <Table.Td>
                          <Group gap={2} wrap="nowrap" style={{ paddingLeft: t.depth * 14 }}>
                            {hasChildren ? (
                              <ActionIcon size="xs" variant="subtle" color="gray" aria-label={expanded.has(group.code) ? `KG ${group.code} zuklappen` : `KG ${group.code} aufklappen`} aria-expanded={expanded.has(group.code)} onClick={() => toggle(group.code)}>
                                {expanded.has(group.code) ? '▾' : '▸'}
                              </ActionIcon>
                            ) : <span style={{ width: 18, display: 'inline-block' }} />}
                            <EditableText value={group.code} monospace dimmed={t.depth > 0} bold={t.depth === 0} title="Nummer ändern" onCommit={(next) => canEdit && update.mutate({ id: group.id, code: next })} />
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap" style={{ paddingLeft: t.depth * 14 }}>
                            {/* The count sits right after the name, not at the far edge of a wide column. */}
                            <div style={{ minWidth: 0, flex: '0 1 auto' }}>
                              <EditableText value={group.name} bold={t.depth === 0} title="Bezeichnung ändern" onCommit={(next) => canEdit && update.mutate({ id: group.id, name: next })} />
                            </div>
                            <Tooltip label="Kostenermittlung: Positionen mit Menge × Kennwert">
                              <ActionIcon
                                size="sm"
                                variant={open?.groupId === group.id ? 'filled' : 'subtle'}
                                color={itemCount(group.id) ? 'brand' : 'gray'}
                                aria-label={`Kostenermittlung KG ${group.code}`}
                                onClick={() => setOpen(open?.groupId === group.id ? null : { groupId: group.id, stage: focusStage })}
                              >
                                {itemCount(group.id) ? String(itemCount(group.id)) : '≡'}
                              </ActionIcon>
                            </Tooltip>
                            {lineCount > 0 && !view.lines && (
                              <Anchor size="xs" component="button" type="button" c="dimmed" style={{ whiteSpace: 'nowrap' }}
                                title={view.linesOf.includes(group.id) ? 'Positionen ausblenden' : `${lineCount} ${lineCount === 1 ? 'Position' : 'Positionen'} unter der Kostengruppe zeigen`}
                                onClick={() => keepView({ ...view, linesOf: view.linesOf.includes(group.id) ? view.linesOf.filter((x) => x !== group.id) : [...view.linesOf, group.id] })}>
                                {view.linesOf.includes(group.id) ? '▾ ausblenden' : '▸ zeigen'}
                              </Anchor>
                            )}
                            {dinNote && (
                              <Tooltip label={dinNote} multiline w={380} style={{ whiteSpace: 'pre-line' }} openDelay={300}>
                                <Text span size="xs" c="dimmed" style={{ cursor: 'help' }} aria-label={`Anmerkung DIN 276 zu KG ${group.code}`}>ⓘ</Text>
                              </Tooltip>
                            )}
                          </Group>
                        </Table.Td>
                        {COST_STAGES.map((s) => {
                          const fromLines = items.some((i) => i.groupId === group.id && i.stage === s.key);
                          const summed = !fromLines && hasChildren && rollup.groups.some((x) => x.parent === group.code && x.stages[s.key] !== null);
                          const active = open?.groupId === group.id && open.stage === s.key;
                          return (
                            <Table.Td key={s.key} style={{ ...(s.key === focusStage ? { background: 'var(--mantine-color-brand-light)' } : {}), ...(active ? { outline: '2px solid var(--mantine-color-brand-5)', outlineOffset: -2 } : {}) }}>
                              {fromLines ? (
                                <div role="button" tabIndex={0} style={{ cursor: 'pointer' }} onClick={() => { setPicked(s.key); setOpen({ groupId: group.id, stage: s.key }); }} onKeyDown={(e) => { if (e.key === 'Enter') { setPicked(s.key); setOpen({ groupId: group.id, stage: s.key }); } }}>
                                  <AmountCell value={show(t.stages[s.key])} readOnly title={hasChildren ? 'Positionen und Untergruppen – anklicken zum Bearbeiten' : 'Summe der Positionen – anklicken zum Bearbeiten'} bold={t.depth === 0} />
                                </div>
                              ) : (
                                <AmountCell
                                  value={show(summed ? t.stages[s.key] : group.own[s.key])}
                                  readOnly={!canEdit || summed || gross}
                                  dimmed={summed}
                                  bold={t.depth === 0 && !summed}
                                  title={summed ? 'Summe der Untergruppen' : gross ? 'Bearbeiten in der Netto-Ansicht' : `${s.label} netto eintragen – oder über ≡ in Positionen aufgliedern`}
                                  onCommit={(next) => update.mutate({ id: group.id, [s.key]: next })}
                                />
                              )}
                            </Table.Td>
                          );
                        })}
                        <Table.Td><AmountCell value={t.committed ? show(t.committed) : null} readOnly dimmed={!t.committed} /></Table.Td>
                        <Table.Td><ControlBar totals={t} reference={reference} /></Table.Td>
                      </Table.Tr>
                      {lineRows(group)}
                      </Fragment>
                    );
                  })}
                  <Table.Tr style={{ borderTop: '2px solid var(--mantine-color-default-border)' }}>
                    {canEdit && <Table.Td />}
                    <Table.Td />
                    <Table.Td><Text fw={700} size="sm">Summe</Text></Table.Td>
                    {COST_STAGES.map((s) => (
                      <Table.Td key={s.key}><AmountCell value={show(rollup.total[s.key])} readOnly bold /></Table.Td>
                    ))}
                    <Table.Td><AmountCell value={show(rollup.committedTotal)} readOnly bold /></Table.Td>
                    <Table.Td />
                  </Table.Tr>
                  {risk > 0 && (
                    <>
                      <TotalRow label={`Risikozuschlag ${formatQuantity(risk)} %`} canEdit={canEdit} values={COST_STAGES.map((s) => show(stageTotals(s.key).risk))} />
                      <TotalRow label="Summe mit Risiko" bold canEdit={canEdit} values={COST_STAGES.map((s) => show(stageTotals(s.key).withRisk))} />
                    </>
                  )}
                  {range > 0 && (
                    <TotalRow
                      label={`Bandbreite ± ${formatQuantity(range)} %`}
                      canEdit={canEdit}
                      text={COST_STAGES.map((s) => {
                        const t = stageTotals(s.key);
                        return t.low === null ? '' : `${formatAmount(show(t.low))} – ${formatAmount(show(t.high))}`;
                      })}
                    />
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            </>
          )}

          {canEdit && data.groups.length > 0 && (
            <form onSubmit={(e) => { e.preventDefault(); if (code.trim() && name.trim()) addGroup.mutate(); }}>
              <Group gap="xs" align="flex-end" pt="xs" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
                <TextInput label="KG" placeholder="z. B. 330" value={code} onChange={(e) => setCode(e.currentTarget.value)} w={110} />
                <TextInput label="Bezeichnung" placeholder="eigene Bezeichnung" value={name} onChange={(e) => setName(e.currentTarget.value)} style={{ flex: 1, maxWidth: 360 }} />
                <Button type="submit" variant="light" disabled={!code.trim() || !name.trim()} loading={addGroup.isPending}>Kostengruppe hinzufügen</Button>
                {dinMissing > 0 ? (
                  <Menu position="top-start" withinPortal>
                    <Menu.Target><Anchor size="xs" component="button" type="button">fehlende Kostengruppen nach DIN 276 ergänzen …</Anchor></Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Label>Ergänzt nur, was fehlt; nichts wird überschrieben</Menu.Label>
                      <Menu.Item onClick={() => outline.mutate(1)}>100 bis 800</Menu.Item>
                      <Menu.Item onClick={() => outline.mutate(2)}>bis zur 2. Ebene</Menu.Item>
                      <Menu.Item onClick={() => outline.mutate(3)}>bis zur 3. Ebene (alle)</Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                ) : data.groups.length < 8 && (din.data?.length ?? 0) === 0 && <Anchor size="xs" component="button" type="button" onClick={() => firstLevel.mutate()}>fehlende Hauptgruppen 100–800 ergänzen</Anchor>}
              </Group>
              <Text size="xs" c="dimmed" mt={4}>Nummern nach DIN 276: 330 steht unter 300, 331 unter 330, 331.1 unter 331. Rechtsklick auf eine Zeile legt eine Untergruppe an.</Text>
            </form>
          )}
        </Section>

        {open && data.groups.some((g) => g.id === open.groupId) && (
          <CostEstimatePanel
            projectId={projectId}
            plan={data}
            group={data.groups.find((g) => g.id === open.groupId)!}
            stage={open.stage}
            variantId={variantId}
            catalog={catalogAccess.data?.allowed ?? false}
            onStage={pick}
            onClose={() => setOpen(null)}
            canEdit={canEdit}
          />
        )}

        <ValuesPanel projectId={projectId} plan={data} stage={focusStage} canEdit={canEdit} />
        {catalogOpen && <BenchmarkCatalog onClose={() => setCatalogOpen(false)} isAdmin={isAdmin} canEdit={me?.permissions.includes('library.manage') ?? false} />}

        <Section title="Aufträge aus der Vergabe" description="Vergebene Aufträge erscheinen hier von selbst, gebucht auf die Kostengruppe der Vergabeeinheit.">
          {data.commitments.length === 0 ? (
            <Text size="sm" c="dimmed">Noch keine vergebenen Aufträge.</Text>
          ) : (
            <Table verticalSpacing={4} className="pst-tabular">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={80}>KG</Table.Th>
                  <Table.Th>Vergabeeinheit</Table.Th>
                  <Table.Th>Auftragnehmende</Table.Th>
                  <Table.Th w={110}>Zuschlag</Table.Th>
                  <Table.Th w={140} ta="right">Auftrag netto (€)</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.commitments.map((c) => (
                  <Table.Tr key={c.sourceId}>
                    <Table.Td>
                      {c.costGroup ?? <Badge size="xs" color="orange" variant="light">ohne KG</Badge>}
                    </Table.Td>
                    <Table.Td>{c.label}</Table.Td>
                    <Table.Td>{c.contractor}</Table.Td>
                    <Table.Td>{formatDate(c.date)}</Table.Td>
                    <Table.Td ta="right">{formatAmount(c.amountNet)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
          {rollup.unassignedCommitted > 0 && (
            <Text size="xs" c="orange.7">Ohne Zuordnung zu einer Kostengruppe der Gliederung: {formatEuro(rollup.unassignedCommitted)} netto.</Text>
          )}
        </Section>
        {context.menu}
        {lineMenu.menu}
      </Stack>
    </div>
  );
}

/** The next free subgroup number below a group: 300 → 310, 330 → 331, 331 → 331.1. */
function suggestChild(code: string, groups: CostGroup[]): string | null {
  const taken = new Set(groups.map((g) => g.code));
  const candidates: string[] = [];
  if (/^\d00$/.test(code)) for (let i = 1; i <= 9; i++) candidates.push(`${code[0]}${i}0`);
  else if (/^\d\d0$/.test(code)) for (let i = 1; i <= 9; i++) candidates.push(`${code.slice(0, 2)}${i}`);
  else for (let i = 1; i <= 99; i++) candidates.push(`${code}.${i}`);
  return candidates.find((c) => !taken.has(c)) ?? null;
}

/** A line under the sum: risk, sum with risk, range - one value or text per stage. */
function TotalRow({ label, values, text, bold, canEdit }: { label: string; values?: (number | null)[]; text?: string[]; bold?: boolean; canEdit: boolean }): JSX.Element {
  return (
    <Table.Tr>
      {canEdit && <Table.Td />}
      <Table.Td />
      <Table.Td><Text size="sm" fw={bold ? 700 : undefined} c={bold ? undefined : 'dimmed'}>{label}</Text></Table.Td>
      {COST_STAGES.map((s, i) => (
        <Table.Td key={s.key}>
          {values ? <AmountCell value={values[i] ?? null} readOnly bold={bold} dimmed={!bold} /> : <Text size="xs" c="dimmed" ta="right" style={{ whiteSpace: 'nowrap' }}>{text?.[i]}</Text>}
        </Table.Td>
      ))}
      <Table.Td />
      <Table.Td />
    </Table.Tr>
  );
}

/**
 * A stage's total. With onClick it is a switch: the chosen stage (active) is
 * filled, the reference stage for cost control keeps its outline and "Bezug".
 */
function Tile({ label, value, highlight, note, active, onClick }: { label: string; value: number | null; highlight?: boolean; note?: string; active?: boolean; onClick?: () => void }): JSX.Element {
  const style = {
    ...(highlight ? { borderColor: 'var(--mantine-color-brand-5)' } : {}),
    ...(active ? { background: 'var(--mantine-color-brand-light)', boxShadow: 'inset 0 -3px 0 var(--pst-accent)' } : {}),
    ...(onClick ? { cursor: 'pointer' } : {}),
  };
  return (
    <Paper
      withBorder
      radius="md"
      p="sm"
      style={style}
      {...(onClick ? {
        role: 'button',
        tabIndex: 0,
        'aria-pressed': active ?? false,
        title: `${label} wählen – Tabelle, Kostenermittlung und Werte zeigen diese Stufe`,
        onClick,
        onKeyDown: (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } },
      } : {})}
    >
      <Text size="xs" c="dimmed">{label}{highlight ? ' · Bezug' : ''}</Text>
      <Text fw={600} size="lg" className="pst-tabular" c={value === null ? 'dimmed' : undefined}>{value === null ? '—' : formatEuro(value)}</Text>
      {note && <Text size="xs" c="dimmed">{note}</Text>}
    </Paper>
  );
}

/**
 * Ordered against planned, as one bar: the track is the reference stage, the
 * fill what is ordered. Past the budget the fill turns red and says so in words.
 */
function ControlBar({ totals, reference }: { totals: CostTotals; reference: CostStage }): JSX.Element | null {
  const planned = totals.stages[reference];
  if (!planned || totals.committed === 0) return null;
  const share = totals.committed / planned;
  const over = share > 1;
  return (
    <Tooltip label={`${formatEuro(totals.committed)} beauftragt von ${formatEuro(planned)} geplant`}>
      <Group gap={6} wrap="nowrap">
        <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--mantine-color-default-border)', overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(share, 1) * 100}%`, height: '100%', borderRadius: 4, background: over ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-brand-6)' }} />
        </div>
        <Text size="xs" c={over ? 'red.7' : 'dimmed'} fw={over ? 600 : undefined} style={{ whiteSpace: 'nowrap' }}>
          {over ? `+${formatPercent((share - 1) * 100)} über` : formatPercent(share * 100)}
        </Text>
      </Group>
    </Tooltip>
  );
}


/** How the tree of one plan is shown; kept per project in this browser. */
interface TreeView {
  depth: DepthChoice;
  /** Folded by hand; null = as the depth says. */
  expanded: string[] | null;
  onlyUsed: boolean;
  /** Lines under every group, or under these groups only. */
  lines: boolean;
  linesOf: string[];
}

const TREE_DEFAULT: TreeView = { depth: 'all', expanded: null, onlyUsed: false, lines: false, linesOf: [] };
const treeKey = (projectId: string): string => `pst.costs.tree.${projectId}`;

function storedTree(projectId: string): TreeView {
  try {
    const raw = window.localStorage.getItem(treeKey(projectId));
    return raw ? { ...TREE_DEFAULT, ...(JSON.parse(raw) as Partial<TreeView>) } : TREE_DEFAULT;
  } catch {
    return TREE_DEFAULT;
  }
}

function storeTree(projectId: string, view: TreeView): void {
  try { window.localStorage.setItem(treeKey(projectId), JSON.stringify(view)); } catch { /* private window */ }
}

/** Up to four variants with short names as buttons; otherwise a searchable list. */
const VARIANT_BUTTON_NAME = 24;

/**
 * Choosing the variant to show. Buttons are quickest, but long names pushed
 * them out of the page, so that nothing could be chosen any more: names are
 * cut in the buttons (full in the tooltip), and with many variants or long
 * names a list with search takes their place.
 */
function VariantChooser({ variants, value, onChange }: { variants: { id: string; code: string; name: string; isPrimary: boolean }[]; value: string; onChange: (v: string) => void }): JSX.Element {
  const full = (v: (typeof variants)[number]): string => `${v.code} ${v.name}${v.isPrimary ? ' (Hauptvariante)' : ''}`;
  const buttons = variants.length <= 4 && variants.every((v) => v.name.length <= VARIANT_BUTTON_NAME * 2);
  const compare = variants.length > 1;
  if (buttons) {
    return (
      <SegmentedControl
        value={value}
        onChange={onChange}
        style={{ maxWidth: '100%' }}
        data={[
          ...variants.map((v) => ({
            value: v.id,
            label: <Text span size="sm" title={full(v)} style={{ display: 'inline-block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>{v.code} {v.name}{v.isPrimary ? ' ·' : ''}</Text>,
          })),
          ...(compare ? [{ value: 'compare', label: 'Vergleich' }] : []),
        ]}
      />
    );
  }
  return (
    <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: '1 1 320px', maxWidth: 560 }}>
      <Select
        aria-label="Variante"
        style={{ flex: 1, minWidth: 0 }}
        searchable
        allowDeselect={false}
        value={value === 'compare' ? null : value}
        placeholder="Vergleich aller Varianten"
        onChange={(v) => v && onChange(v)}
        data={variants.map((v) => ({ value: v.id, label: full(v) }))}
        comboboxProps={{ withinPortal: true, width: 'max-content', position: 'bottom-start' }}
        maxDropdownHeight={360}
      />
      {compare && <Button variant={value === 'compare' ? 'filled' : 'default'} onClick={() => onChange('compare')}>Vergleich</Button>}
    </Group>
  );
}
